import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { IconChart, IconCopy, IconLayers, IconPlus, IconSettings, IconTrash } from "../icons";
import { useShell } from "./AdminShell";

export default function Dashboard() {
  const { products, refreshNav } = useShell();
  const nav = useNavigate();
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deletingSelected, setDeletingSelected] = useState(false);

  const duplicate = async (demoId: string) => {
    try {
      setDuplicatingId(demoId);
      const cloned = await api.duplicateDemo(demoId);
      await refreshNav();
      nav(`/admin/demos/${cloned.demo.id}/basics`);
    } catch (e: any) {
      alert(e.message);
    } finally {
      setDuplicatingId(null);
    }
  };

  const removeDemo = async (demoId: string, demoTitle: string) => {
    if (!confirm(`Delete "${demoTitle}"? Its screens, survey and analytics are removed and the public link stops working.`)) return;
    try {
      setDeletingId(demoId);
      await api.deleteDemo(demoId);
      setSelectedIds((prev) => {
        if (!prev.has(demoId)) return prev;
        const next = new Set(prev);
        next.delete(demoId);
        return next;
      });
      await refreshNav();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const removeProduct = async (productId: string, productName: string) => {
    if (!confirm(`Delete product "${productName}"? This can't be undone.`)) return;
    try {
      setDeletingProductId(productId);
      await api.deleteProduct(productId);
      await refreshNav();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setDeletingProductId(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllInProduct = (productDemos: { id: string }[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = productDemos.length > 0 && productDemos.every((d) => next.has(d.id));
      if (allSelected) {
        productDemos.forEach((d) => next.delete(d.id));
      } else {
        productDemos.forEach((d) => next.add(d.id));
      }
      return next;
    });
  };

  const deleteSelected = async () => {
    const count = selectedIds.size;
    if (count === 0) return;
    if (!confirm(`Delete ${count} selected demo${count === 1 ? "" : "s"}? Their screens, survey and analytics will be removed and public links will stop working.`)) return;
    try {
      setDeletingSelected(true);
      for (const id of selectedIds) {
        await api.deleteDemo(id);
      }
      setSelectedIds(new Set());
      await refreshNav();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setDeletingSelected(false);
    }
  };

  if (products.length === 0) {
    return (
      <div className="panel empty">
        <span className="empty-icon"><IconLayers /></span>
        <h1>Build your first demo</h1>
        <p className="muted">
          A product holds one or more click-through demos. Start with the product, then the wizard walks you through
          the survey, outcomes, screenshots and hotspots.
        </p>
        <Link to="/admin/new" className="btn btn-primary"><IconPlus />Add a product</Link>
      </div>
    );
  }
  const demos = products.flatMap((p) => p.demos);
  const live = demos.filter((d) => d.status === "published").length;
  return (
    <div className="stack stack-l">
      <div className="page-head">
        <div><div className="eyebrow">Workspace</div><h1>Demos</h1></div>
        <span className="spacer" />
        {selectedIds.size > 0 && (
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <span className="small muted">{selectedIds.size} selected</span>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              disabled={deletingSelected}
              onClick={deleteSelected}
            >
              <IconTrash />{deletingSelected ? "Deleting…" : `Delete selected (${selectedIds.size})`}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={deletingSelected}
              onClick={() => setSelectedIds(new Set())}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      <div className="overview stats">
        <div className="stat"><b>{products.length}</b><span>Products</span></div>
        <div className="stat"><b>{demos.length}</b><span>Demos</span></div>
        <div className="stat"><b>{live}</b><span>Live now</span></div>
      </div>
      {products.map((p) => (
        <section key={p.id} className="panel panel-flush">
          <div className="panel-head">
            <span className="swatch" style={{ background: p.brandColor }} />
            <h2>{p.name}</h2>
            <span className="mono small muted">/{p.slug}</span>
            <span className="spacer" />
            <Link to={`/admin/products/${p.id}`} className="btn btn-sm"><IconSettings />Product settings</Link>
          </div>
          {p.demos.length === 0 ? (
            <div className="row" style={{ padding: "14px 18px", alignItems: "center" }}>
              <p className="muted small" style={{ margin: 0 }}>No demos yet. <Link to={`/admin/products/${p.id}`}>Add one</Link>.</p>
              <span className="spacer" />
              <button
                type="button"
                className="btn btn-sm btn-ghost btn-danger"
                title="Delete empty product"
                disabled={deletingProductId === p.id}
                onClick={() => removeProduct(p.id, p.name)}
              >
                <IconTrash />{deletingProductId === p.id ? "Deleting…" : "Delete product"}
              </button>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 36, paddingLeft: 18, paddingRight: 0 }}>
                    <input
                      type="checkbox"
                      title="Select all demos in this product"
                      aria-label="Select all demos in this product"
                      checked={p.demos.length > 0 && p.demos.every((d) => selectedIds.has(d.id))}
                      onChange={() => toggleSelectAllInProduct(p.demos)}
                    />
                  </th>
                  <th>Demo</th>
                  <th>Status</th>
                  <th>Screens</th>
                  <th>Last edited</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {p.demos.map((d) => (
                  <tr key={d.id} className={selectedIds.has(d.id) ? "row-selected" : ""}>
                    <td style={{ width: 36, paddingLeft: 18, paddingRight: 0 }}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${d.title}`}
                        checked={selectedIds.has(d.id)}
                        onChange={() => toggleSelect(d.id)}
                      />
                    </td>
                    <td><Link to={`/admin/demos/${d.id}/basics`}>{d.title}</Link></td>
                    <td><span className={`pill ${d.status === "published" ? "pill-live" : "pill-draft"}`}>{d.status === "published" ? "Live" : "Draft"}</span></td>
                    <td>{d.stepCount}</td>
                    <td className="muted small">{new Date(d.updatedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
                    <td className="cell-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        title="Duplicate demo"
                        disabled={duplicatingId === d.id || deletingId === d.id || deletingSelected}
                        onClick={() => duplicate(d.id)}
                      >
                        <IconCopy />{duplicatingId === d.id ? "Duplicating…" : "Duplicate"}
                      </button>
                      <Link to={`/admin/demos/${d.id}/analytics`} className="btn btn-sm btn-ghost"><IconChart />Analytics</Link>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost btn-danger"
                        title="Delete demo"
                        disabled={deletingId === d.id || duplicatingId === d.id || deletingSelected}
                        onClick={() => removeDemo(d.id, d.title)}
                      >
                        <IconTrash />{deletingId === d.id ? "Deleting…" : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ))}
    </div>
  );
}

